import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { markNotificationAsRead } from '../../services/notificationService';
import { supabase } from '../../utils/supabase';
import Button from '../common/Button';
import { Caption, Typography } from '../common/Typography';
import { StatusBadge } from '../common';
import CollapsibleNotificationItem from '../notifications/CollapsibleNotificationItem';

/**
 * GuestAccountMergeNotificationItem Component
 * Enhanced notification item for guest account merge notifications with accept/decline actions
 */
const GuestAccountMergeNotificationItem = ({ 
  notification, 
  currentUser,
  onUpdate,
  onClose 
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isGuestMerged, setIsGuestMerged] = useState(false);
  const [isGuestDeclined, setIsGuestDeclined] = useState(false);

  const notificationData = notification.data || {};
  const guestId = notificationData.guest_id;
  const clubId = notificationData.club_id;
  const clubName = notificationData.club_name || 'the club';
  const guestName = notificationData.guest_name || 'Guest Account';
  const guestCreatedAt = notificationData.guest_created_at;
  const counts = notificationData.counts || {};

  // Check if guest account has been merged or declined
  useEffect(() => {
    const checkGuestMergeStatus = async () => {
      if (!guestId) return;

      try {
        const { data, error } = await supabase
          .from('players')
          .select('merged_at, merged_into_user_id, guest_merge_declined_at')
          .eq('id', guestId)
          .single();

        if (error) {
          console.error('Error checking guest merge status:', error);
          return;
        }

        // Guest is merged if merged_at is not null
        if (data && data.merged_at) {
          setIsGuestMerged(true);
        }
        
        // Guest is declined if guest_merge_declined_at is not null
        if (data && data.guest_merge_declined_at) {
          setIsGuestDeclined(true);
        }
      } catch (err) {
        console.error('Error checking guest merge status:', err);
      }
    };

    checkGuestMergeStatus();
  }, [guestId]);

  const handleGoToClub = async () => {
    if (!clubId || !guestId) return;

    try {
      setLoading(true);
      
      // Mark notification as read
      if (notification.id && !notification.is_read) {
        try {
          await markNotificationAsRead(notification.id);
        } catch (err) {
          console.error('Error marking notification as read:', err);
          // Continue even if marking as read fails
        }
      }

      // Close notifications modal
      if (onClose) {
        onClose();
      }

      // Navigate to clubs page with guest context
      // The club join flow will detect this and show all options (merge, start fresh, decline) in the modal
      navigate('/clubs', {
        state: {
          mergeGuestId: guestId,
          mergeClubId: clubId,
          fromNotification: true
        }
      });

      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error navigating to club join:', err);
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const detailsContent = (
    <>
      <div className="text-sm">
        <span className="text-text-muted">Guest Name: </span>
        <Typography variant="strong" as="span">{guestName}</Typography>
      </div>
      {guestCreatedAt && (
        <div className="text-sm">
          <span className="text-text-muted">Created: </span>
          <Typography variant="strong" as="span">{formatDate(guestCreatedAt)}</Typography>
        </div>
      )}
      {counts && (
        <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-border-medium">
          <div className="text-sm">
            <span className="text-text-muted">Games: </span>
            <Typography variant="strong" as="span">{counts.games || 0}</Typography>
          </div>
          {counts.partnerships !== undefined && (
            <div className="text-sm">
              <span className="text-text-muted">Partnerships: </span>
              <Typography variant="strong" as="span">{counts.partnerships || 0}</Typography>
            </div>
          )}
          {counts.sessions !== undefined && (
            <div className="text-sm">
              <span className="text-text-muted">Sessions: </span>
              <Typography variant="strong" as="span">{counts.sessions || 0}</Typography>
            </div>
          )}
        </div>
      )}
    </>
  );

  const metadata = clubName && (
    <Caption>Club: {clubName}</Caption>
  );

  // Action button or status badge
  const actionContent = !isGuestMerged && !isGuestDeclined ? (
    <Button
      variant="primary"
      size="small"
      onClick={handleGoToClub}
      disabled={loading}
    >
      {loading ? 'Loading...' : 'Go to Club'}
    </Button>
  ) : isGuestMerged ? (
    <StatusBadge variant="success" size="small">Merged</StatusBadge>
  ) : (
    <StatusBadge variant="warning" size="small">Declined</StatusBadge>
  );

  return (
    <>
      <CollapsibleNotificationItem
        notification={notification}
        borderColor="blue"
        badgeVariant="info"
        detailsContent={detailsContent}
        metadata={metadata}
      >
        {actionContent}
      </CollapsibleNotificationItem>
    </>
  );
};

export default GuestAccountMergeNotificationItem;
